import type {IssueTimelineItemFragment} from '../components/timeline/IssueTimelineItem'

type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends ReadonlyArray<infer ElementType> ? ElementType : never

type WithIndex<T> = T & {__index: number}
type ArrayWithIndex<T> = Array<WithIndex<T>>

export type TimelineItem = IssueTimelineItemFragment | null

const ROLLUP_TIME_CUTOFF = 1000 * 60 * 60 * 24 * 7 // a week - this is in parity with rails
const ISSUE_FIELD_ROLLUP_TIME_CUTOFF = 1000 * 60 * 60 // 1 hour - tighter grouping for issue field events

const ENABLED_ROLLUP_TYPE_MAP: Record<string, Set<string>> = {
  LabeledEvent: new Set(['LabeledEvent', 'UnlabeledEvent']),
  UnlabeledEvent: new Set(['LabeledEvent', 'UnlabeledEvent']),
  CrossReferencedEvent: new Set(['CrossReferencedEvent']),
  ReferencedEvent: new Set(['ReferencedEvent']),
  AssignedEvent: new Set(['AssignedEvent', 'UnassignedEvent']),
  UnassignedEvent: new Set(['AssignedEvent', 'UnassignedEvent']),
  AddedToProjectV2Event: new Set(['AddedToProjectV2Event', 'RemovedFromProjectV2Event']),
  RemovedFromProjectV2Event: new Set(['AddedToProjectV2Event', 'RemovedFromProjectV2Event']),
  MilestonedEvent: new Set(['MilestonedEvent', 'DemilestonedEvent']),
  DemilestonedEvent: new Set(['MilestonedEvent', 'DemilestonedEvent']),
  SubIssueAddedEvent: new Set(['SubIssueAddedEvent']),
  SubIssueRemovedEvent: new Set(['SubIssueRemovedEvent']),
  BlockedByAddedEvent: new Set(['BlockedByAddedEvent']),
  BlockedByRemovedEvent: new Set(['BlockedByRemovedEvent']),
  BlockingAddedEvent: new Set(['BlockingAddedEvent']),
  BlockingRemovedEvent: new Set(['BlockingRemovedEvent']),
  IssueFieldAddedEvent: new Set(['IssueFieldAddedEvent', 'IssueFieldChangedEvent', 'IssueFieldRemovedEvent']),
  IssueFieldChangedEvent: new Set(['IssueFieldAddedEvent', 'IssueFieldChangedEvent', 'IssueFieldRemovedEvent']),
  IssueFieldRemovedEvent: new Set(['IssueFieldAddedEvent', 'IssueFieldChangedEvent', 'IssueFieldRemovedEvent']),
  IssueCommentPinnedEvent: new Set(['IssueCommentPinnedEvent', 'IssueCommentUnpinnedEvent']),
  IssueCommentUnpinnedEvent: new Set(['IssueCommentPinnedEvent', 'IssueCommentUnpinnedEvent']),
}

const ROLLUP_SIZE_LIMIT = 20 // Maximum number of events to roll up

const ISSUE_FIELD_EVENT_TYPES = new Set(['IssueFieldAddedEvent', 'IssueFieldChangedEvent', 'IssueFieldRemovedEvent'])

const getCreatedAt = (event: TimelineItem): Date | undefined => {
  if (event?.__typename && ENABLED_ROLLUP_TYPE_MAP[event.__typename]) {
    return (event.createdAt && new Date(event.createdAt)) || undefined
  }

  return undefined
}

const canPerformSpecificEventRollup = (event: NonNullable<TimelineItem>): boolean => {
  if (event.__typename === 'CrossReferencedEvent' && event.willCloseTarget) {
    return false
  }

  return true
}

const eventCanRollup = <T extends TimelineItem>(event: T, nextEvent: T): boolean => {
  if (!event || !nextEvent) {
    return false
  }

  // `actor` is selected on `TimelineEvent` members only — `IssueComment` carries an `author` instead.
  const actor = 'actor' in event ? event.actor : undefined
  if (!actor) {
    return false
  }

  if (!ENABLED_ROLLUP_TYPE_MAP[event.__typename]?.has(nextEvent.__typename)) {
    return false
  }

  const nextActor = 'actor' in nextEvent ? nextEvent.actor : undefined
  if (actor.login !== nextActor?.login) {
    return false
  }

  if (!canPerformSpecificEventRollup(event) || !canPerformSpecificEventRollup(nextEvent)) {
    return false
  }

  const createdAt = getCreatedAt(event)
  const nextCreatedAt = getCreatedAt(nextEvent)
  const timeCutoff = ISSUE_FIELD_EVENT_TYPES.has(event.__typename) ? ISSUE_FIELD_ROLLUP_TIME_CUTOFF : ROLLUP_TIME_CUTOFF

  if (
    createdAt === undefined ||
    nextCreatedAt === undefined ||
    Math.abs(createdAt.getTime() - nextCreatedAt.getTime()) > timeCutoff
  ) {
    return false
  }
  return true
}

const totalRolledUpEvents = <T>(rollups: Record<string, T[]>): number => {
  let total = 0
  for (const key in rollups) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    total += rollups[key]!.length
  }

  return total
}

export interface RolledUpTimelineItem<T extends TimelineItem> {
  item: T | null
  rollupGroup: Record<string, ArrayWithIndex<T>> | undefined
}

export const rollupEvents = <T extends TimelineItem>(events: T[]): Array<RolledUpTimelineItem<T>> => {
  const rolledUpEvents: Array<RolledUpTimelineItem<T>> = []
  let index = 0
  while (index < events.length) {
    const rollups = rollupCurrentEvent(events, index)

    // This includes the current event
    const totalEventsRolledUp = totalRolledUpEvents(rollups)
    const currentEvent = events[index]

    if (rollups) removeDuplicateRollupEvents(rollups)

    rolledUpEvents.push({
      item: currentEvent ?? null,
      rollupGroup: totalEventsRolledUp > 1 ? rollups : undefined,
    })

    // For null/undefined events the rollups will be empty but we need to increment at least 1.
    index += Math.max(totalEventsRolledUp, 1)
  }

  return rolledUpEvents
}

const rollupCurrentEvent = <T extends TimelineItem>(events: T[], index: number): Record<string, ArrayWithIndex<T>> => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  let currentEvent = events[index]!
  const rollups = [currentEvent]

  while (index + 1 < events.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const nextEvent = events[index + 1]!
    if (!eventCanRollup(currentEvent, nextEvent) || rollups.length >= ROLLUP_SIZE_LIMIT) {
      break
    }

    currentEvent = nextEvent
    rollups.push(currentEvent)
    index++
  }

  return partitionRollups(rollups)
}

const partitionRollups = <T extends TimelineItem>(rollups: T[]): Record<string, ArrayWithIndex<T>> => {
  // Partition the distinct rolled up groups by __typename
  const distinctRollups: Record<string, ArrayWithIndex<T>> = {}
  let index = 0
  for (const rollup of rollups) {
    if (!rollup) {
      continue
    }

    const key = rollup.__typename
    if (!distinctRollups[key]) {
      distinctRollups[key] = []
    }

    // Add the index to the rollup so we can sort them later by their position in the timeline
    distinctRollups[key].push({__index: index, ...rollup})
    index++
  }

  return distinctRollups
}

const removeDuplicateRollupEvents = <T extends TimelineItem>(rollups: Record<string, ArrayWithIndex<T>>) => {
  deduplicateIssueFieldEvents(rollups)

  for (const value of Object.values(rollups)) {
    const uniqueIds = new Set()

    for (let i = 0; i < value.length; i++) {
      const event = value[i]
      if (!event) {
        continue
      }
      const {__typename: eventName} = event || {}
      const id = getEventItemId(event)

      if (id) {
        const uniqueKey = `${id}-${eventName}`
        if (uniqueIds.has(uniqueKey)) {
          value.splice(i--, 1) // Remove duplicate and adjust index
        } else {
          uniqueIds.add(uniqueKey)
        }
      }
    }
  }
}

/**
 * For issue field events, we want to keep only the latest event per field name.
 * This consolidates multiple changes to the same field into just the final value.
 * For example: "set Effort to Large" then "changed Effort to Small" becomes just "Effort (Small)"
 */
const deduplicateIssueFieldEvents = <T extends TimelineItem>(rollups: Record<string, ArrayWithIndex<T>>) => {
  const fieldNameToLatest: Record<string, {event: WithIndex<T>; eventType: string}> = {}

  // Find the latest event for each field name across all event types
  for (const eventType of ISSUE_FIELD_EVENT_TYPES) {
    const events = rollups[eventType]
    if (!events) continue

    for (const event of events) {
      if (!event) continue

      const fieldName = getIssueFieldName(event)
      if (!fieldName) continue

      const existing = fieldNameToLatest[fieldName]

      if (!existing || event.__index > existing.event.__index) {
        fieldNameToLatest[fieldName] = {event, eventType}
      }
    }
  }

  // Rebuild each event type array with only the latest events
  for (const eventType of ISSUE_FIELD_EVENT_TYPES) {
    if (!rollups[eventType]) continue

    rollups[eventType] = Object.values(fieldNameToLatest)
      .filter(item => item.eventType === eventType)
      .map(item => item.event)
  }
}

const getIssueFieldName = (item: TimelineItem): string | undefined => {
  switch (item?.__typename) {
    case 'IssueFieldAddedEvent':
    case 'IssueFieldChangedEvent':
    case 'IssueFieldRemovedEvent':
      return item.issueField?.name
    default:
      return undefined
  }
}

const getEventItemId = (item: TimelineItem): string | undefined => {
  const type = item?.__typename

  switch (type) {
    case 'LabeledEvent':
    case 'UnlabeledEvent':
      return item?.label?.id
    case 'AssignedEvent':
    case 'UnassignedEvent':
      return item?.assignee?.id
    case 'IssueCommentPinnedEvent':
    case 'IssueCommentUnpinnedEvent':
      return item?.issueComment?.id
    default:
      return undefined
  }
}
